import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Button, DatePicker, CheckboxDropdown } from 'yankees-react-utils';
import useOpposingTeamSelector from '../../../../utils/useOpposingTeamSelector';
import useConfig from '../../../../utils/useConfig';
import usePlayerSelector from '../../../../utils/usePlayerSelector';

const getHandednessOptions = (opposingTeam, batter, isLhb, translate) => {
    const handednessOptions = [
        { name: `${translate('baseball.abbr-versus')} ${translate('baseball.lhh')}`, value: 2 },
        { name: `${translate('baseball.abbr-versus')} ${translate('baseball.rhh')}`, value: 3 },
    ];
    return [
        ...(opposingTeam.LevelIDData === 8 ? [{ name: `${translate('baseball.abbr-versus')} ${batter.FullName}`, value: 1 }] : []),
        ...(isLhb ? handednessOptions : handednessOptions.reverse()),
    ];
};
const Filters = ({ pitcherId, isLhb, year, setYear, startDate, setStartDate, endDate, setEndDate, handedness, setHandedness, reportYearsPitching,
    pitcherLevelCodes, setPitcherLevelCodes, levels = null, batter: batterOverride = null, initialHandedness = null }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear, UsePreviousYearData } = useConfig();
    const [innerYear, setInnerYear] = useState(year);
    const [innerStartDate, setInnerStartDate] = useState(startDate);
    const [innerEndDate, setInnerEndDate] = useState(endDate);
    const [innerHandedness, setInnerHandedness] = useState(handedness);
    const [innerLevelcode, setInnerLevelcode] = useState(pitcherLevelCodes[pitcherId] ?? -1);
    const [showInvalidDateRange, setShowInvalidDateRange] = useState(false);

    const opposingTeam = useOpposingTeamSelector();
    const selectedBatter = usePlayerSelector();
    const batter = batterOverride || selectedBatter;
    const levelcode = useMemo(() => pitcherLevelCodes[pitcherId] ?? -1, [pitcherId, pitcherLevelCodes]);
    const singleYearOptions = window.$IsDugoutApp ? [CurrentYear] : [CurrentYear, CurrentYear - 1, CurrentYear - 2];
    const yearOptions = [
        { name: `${CurrentYear - 1} - ${CurrentYear}`, value: -1 },
        ...(singleYearOptions.map((y) => ({ name: y, value: y }))),
    ];

    // eslint-disable-next-line no-nested-ternary
    const rypSY = window.$IsDugoutApp ? (year?.value > 0 ? year.value : CurrentYear - 1) : (new Date(startDate)).getFullYear();
    // eslint-disable-next-line no-nested-ternary
    const rypEY = window.$IsDugoutApp ? (year?.value > 0 ? year.value : CurrentYear) : (new Date(endDate)).getFullYear();
    const levelOpts = useMemo(() => {
        if (!levels || !reportYearsPitching) return [];
        return [...new Set(reportYearsPitching?.filter((to) => [7, 8].indexOf(to.LevelID) >= 0 && to.YearID >= rypSY && to.YearID <= rypEY)
            .map((to) => ({ LevelID: to.LevelID, Level: to.Level }))
            .map((o) => JSON.stringify(o)))].map((s) => JSON.parse(s))
            .map((o) => ({ name: o.Level.Abbr, value: o.LevelID, checked: levelcode === -2 || ((levelcode ?? -1) === -1 && levels != null && levels.indexOf(o.LevelID) >= 0) || o.LevelID === levelcode }));
    }, [levels, reportYearsPitching, rypSY, rypEY, levelcode]);
    const [innerLevelIds, setInnerLevelIds] = useState(levelOpts ?? []);
    const handednessOptions = useMemo(() => getHandednessOptions(opposingTeam, batter, isLhb, translate), [opposingTeam, batter, isLhb, translate]);

    useEffect(() => {
        const initialYear = yearOptions[UsePreviousYearData ? 0 : 1];
        const initialHandednessOption = handednessOptions.find((option) => option.value === initialHandedness) || handednessOptions[0];
        if (year == null) {
            setYear(initialYear);
            setInnerYear(initialYear);
        }
        if (handedness == null) {
            setHandedness(initialHandednessOption);
            setInnerHandedness(initialHandednessOption);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const usesYearFilter = window.$IsDugoutApp;
    const onFilter = () => {
        if (!usesYearFilter && innerStartDate > innerEndDate) {
            setShowInvalidDateRange(true);
            return;
        }

        setShowInvalidDateRange(false);
        setYear(innerYear);
        setStartDate(innerStartDate);
        setEndDate(innerEndDate);
        setHandedness(innerHandedness);
        setPitcherLevelCodes({ ...pitcherLevelCodes, [pitcherId]: innerLevelcode });
    };

    useEffect(() => {
        setInnerLevelIds(innerLevelIds.length > 0 && innerLevelIds.filter((x) => x.checked).length > 0 ? innerLevelIds : levelOpts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pitcherId, levels, reportYearsPitching, rypSY, rypEY, levelcode]);

    const updateLevelCheckboxes = (o, key, e) => {
        const updatedCheckboxes = innerLevelIds.map((t) => {
            const mappedOption = {
                ...t,
                checked: t.name === o.name ? e.target.checked : t.checked,
            };
            return mappedOption;
        });
        setInnerLevelIds(updatedCheckboxes);
        const checked = updatedCheckboxes?.filter((x) => x.checked);
        let lc = -1;
        switch (checked.length) {
            case 0:
                lc = -1;
                break;
            case 1:
                lc = checked[0].value;
                break;
            case 2:
                lc = -2;
                break;
            default:
                lc = -1;
        }
        setInnerLevelcode(lc);
    };

    const checkedLevels = (innerLevelIds && ((innerLevelIds?.filter((x) => x.checked) ?? []).map((x) => x.value))) ?? [];
    let levelTitle = '';
    switch (innerLevelcode ?? levelcode) {
        case -2: levelTitle = 'MLB+MIN';
            break;
        case -1:
            if (checkedLevels) {
                if (checkedLevels.length === 2) { levelTitle = 'MLB+MIN'; }
                if (checkedLevels.length === 1) { levelTitle = checkedLevels[0] === 8 ? 'MLB' : 'MIN'; }
                if (checkedLevels.length === 0) { levelTitle = '--'; }
            } else { levelTitle = ''; }
            break;
        case 8: levelTitle = 'MLB';
            break;
        case 7: levelTitle = 'MIN';
            break;
        default:
            if (checkedLevels) {
                if (checkedLevels.length === 2) { levelTitle = 'MLB+MIN'; }
                if (checkedLevels.length === 1) { levelTitle = checkedLevels[0] === 8 ? 'MLB' : 'MIN'; }
                if (checkedLevels.length === 0) { levelTitle = '--'; }
            } else { levelTitle = ''; }
    }

    return (
        <div className="d-flex justify-content-end align-items-center">
            {usesYearFilter
                ? (
                    <Dropdown
                        align="end"
                        options={yearOptions}
                        value={innerYear}
                        onSelect={setInnerYear}
                    />
                )
                : (
                    <>
                        <div style={{ width: '120px', flexShrink: 0 }}>
                            <DatePicker
                                size="sm"
                                maxDate={new Date()}
                                value={innerStartDate}
                                onSelect={(value) => setInnerStartDate(value)}
                                openToDate={innerStartDate}
                            />
                        </div>
                        <span className="mx-2">{translate('common.to')}</span>
                        <div style={{ width: '120px', flexShrink: 0 }}>
                            <DatePicker
                                size="sm"
                                maxDate={new Date()}
                                value={innerEndDate}
                                onSelect={(value) => setInnerEndDate(value)}
                                openToDate={innerEndDate}
                            />
                        </div>
                    </>
                )}
            <div className="mx-2">
                <Dropdown
                    align="end"
                    options={handednessOptions}
                    value={innerHandedness}
                    onSelect={setInnerHandedness}
                />
            </div>
            {opposingTeam.LevelIDData === 8 && innerHandedness?.value !== 1 && levelOpts && levelOpts.length > 0 && innerLevelIds && innerLevelIds.length > 0 && (
                <>
                    <label className="ms-1 me-1 flex-shrink-0">{translate('baseball.level_one')}:</label>
                    <CheckboxDropdown
                        size="sm"
                        style={{ width: '75px' }}
                        options={innerLevelIds}
                        title={levelTitle}
                        titleOverrideFormat
                        onChange={updateLevelCheckboxes}
                        className="me-1"
                    />
                </>
            )}
            <Button
                variant="primary"
                onClick={onFilter}
            >
                {translate('common.update')}
            </Button>
            {showInvalidDateRange
                && (
                    <span className="text-danger ms-2">
                        {translate('sections.opponent.player.invalid-combination')}
                    </span>
                )}
        </div>
    );
};

export default Filters;
